import type { ReactNode } from "react";
import type { Catalog } from "../types";
import { DocState, hasGloria } from "../state";
import { Card, ReadingBlock } from "../ui/display";
import { Field, TextArea, Checkbox, Button, IconButton } from "../ui/controls";
import { Icons, WhatsAppGlyph } from "../ui/icons";
import { useIsMobile } from "../ui/useIsMobile";
import { YouTubeLink } from "../ui/YouTubeLink";
import { ScoreLink } from "../ui/ScoreLink";
import { SongPicker } from "./SongPicker";
import { OrdinaryPicker } from "./OrdinaryPicker";
import { IntentionsEditor } from "./IntentionsEditor";
import { IntentionRefrainPicker } from "./IntentionRefrainPicker";

type TextBlockKey = "motAccueil" | "avant1" | "avant2";
type BlockKey = TextBlockKey | "intentions";

function SectionIcon({ children }: { children: ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "var(--radius-md)", background: "var(--accent-soft)", color: "var(--accent-text)", flexShrink: 0 }}>
      {children}
    </span>
  );
}

function RegenButton({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <IconButton aria-label="Régénérer cette phrase" variant="sacred" size="sm" onClick={onClick} disabled={busy}>
      <Icons.Refresh size={14} />
    </IconButton>
  );
}

// Must stay at module level: a component declared inside MassDocument is a new
// type on every render, so React remounts its textarea on each keystroke/autosave
// and the field loses focus while typing.
function AiField({ label, value, onChange, onRegen, busy, rows = 2, placeholder, hint }: { label: string; value: string; onChange: (v: string) => void; onRegen: () => void; busy: boolean; rows?: number; placeholder?: string; hint?: ReactNode }) {
  return (
    <Field label={label} action={<RegenButton onClick={onRegen} busy={busy} />} hint={hint}>
      <TextArea rows={rows} value={value} onChange={onChange} placeholder={placeholder} />
    </Field>
  );
}

export function MassDocument({
  doc,
  catalog,
  onPatch,
  onRegen,
  aiBlock,
  onDownload,
  onDownloadFeuille,
  onShare,
  saveLabel,
  onRegenSong,
  songAiMoment,
  onRefillRefrain,
  refrainBusy,
}: {
  doc: DocState;
  catalog: Catalog;
  onPatch: (p: Partial<DocState>) => void;
  onRegen: (b: BlockKey) => void;
  aiBlock: BlockKey | null;
  onDownload: () => void;
  onDownloadFeuille: () => void;
  onShare: () => void;
  saveLabel: string;
  onRegenSong: (moment: string) => void;
  songAiMoment: string | null;
  onRefillRefrain: () => void;
  refrainBusy: boolean;
}) {
  const l = doc.liturgy;
  const isMobile = useIsMobile();
  // Wiring for one animateur text block (value, edit, regenerate) — spread into <AiField>.
  const textBlock = (block: TextBlockKey) => ({
    value: doc[block],
    onChange: (v: string) => onPatch({ [block]: v } as Partial<DocState>),
    onRegen: () => onRegen(block),
    busy: aiBlock === block,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px", paddingBottom: isMobile ? "0" : "152px" }}>
      {/* I · Ouverture */}
      <Card eyebrow="I · Ouverture" title="Accueil & entrée" aside={<SectionIcon><Icons.Hand size={17} /></SectionIcon>}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <AiField label="Mot d'accueil" {...textBlock("motAccueil")} rows={5} placeholder="Cliquez sur « Générer un brouillon automatiquement » ou rédigez ici…" hint="Proposé à partir des lectures — modifiable." />
          <SongPicker label="Chant d'entrée" moment="entree" season={l.seasonKey} catalog={catalog} value={doc.entree} onChange={(c) => onPatch({ entree: c })} onRegen={() => onRegenSong("entree")} regenerating={songAiMoment === "entree"} />
          <OrdinaryPicker value={doc.ordinary} ordinaries={catalog.ordinaries} onChange={(v) => onPatch({ ordinary: v })} hint={`Kyrie${hasGloria(l.seasonKey) ? ", Gloria" : " (pas de Gloria en ce temps)"}, Sanctus, Anamnèse, Agneau de Dieu.`} />
          <Field label="Prière d'ouverture (prêtre)" hint="Récupérée depuis Prions en Église — modifiable.">
            <TextArea rows={2} value={doc.oraisonOuverture} onChange={(v) => onPatch({ oraisonOuverture: v })} placeholder="À compléter…" />
          </Field>
        </div>
      </Card>

      {/* II · Liturgie de la Parole */}
      <Card eyebrow="II · Liturgie de la Parole" title="Lectures & psaume" aside={<SectionIcon><Icons.Book size={17} /></SectionIcon>}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {l.first && <AiField label="Avant la première lecture" {...textBlock("avant1")} placeholder="Phrase d'introduction…" />}
          {l.first && <ReadingBlock label="Première lecture" intro={l.first.intro} reference={l.first.ref} title={l.first.title} text={l.first.text} />}
          {l.psalm && (() => {
            const psNum = (l.psalm.ref.match(/\d+/) || [])[0];
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--text-sm)", minWidth: 0, flex: "1 1 auto" }}>
                    Psaume <span style={{ fontFamily: "var(--font-ref)", color: "var(--accent-text)", fontWeight: 400 }}>{l.psalm.ref}</span>
                  </span>
                  {psNum && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <ScoreLink title={`Psaume ${psNum}`} query={`psaume ${psNum} partition`} />
                      <YouTubeLink title={`Psaume ${psNum}`} query={`psaume ${psNum} refrain`} />
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)" }}>
                      Refrain <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>— modifiable</span>
                    </span>
                    <IconButton aria-label="Rétablir le refrain officiel (AELF)" variant="ghost" size="sm" onClick={onRefillRefrain} disabled={refrainBusy}>
                      <Icons.Refresh size={14} />
                    </IconButton>
                  </div>
                  <TextArea rows={2} serif value={doc.psalmRefrain || ""} onChange={(v) => onPatch({ psalmRefrain: v })} placeholder="Refrain du psaume" />
                </div>
              </div>
            );
          })()}
          {l.second && <AiField label="Avant la deuxième lecture" {...textBlock("avant2")} placeholder="Phrase d'introduction…" />}
          {l.second && <ReadingBlock label="Deuxième lecture" intro={l.second.intro} reference={l.second.ref} title={l.second.title} text={l.second.text} />}
          {l.acclamation && (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <span style={{ fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--text-sm)" }}>Acclamation à l'Évangile</span>
              <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "var(--text-md)", color: "var(--text-secondary)", whiteSpace: "pre-wrap" }}>{l.acclamation.text}</span>
            </div>
          )}
          {l.gospel && <ReadingBlock label="Évangile" intro={l.gospel.intro} reference={l.gospel.ref} title={l.gospel.title} text={l.gospel.text} />}
          <Checkbox checked={doc.includeReadingsText} onChange={(v) => onPatch({ includeReadingsText: v })} label="Inclure le texte intégral des lectures dans le document Word" />
        </div>
      </Card>

      {/* III · Prière universelle */}
      <Card eyebrow="III · Prière universelle" title="Intentions" aside={<SectionIcon><Icons.Hand size={17} /></SectionIcon>}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <IntentionRefrainPicker value={doc.prierePuRefrain} onChange={(v) => onPatch({ prierePuRefrain: v })} hint="Chanté ou dit par l'assemblée après chaque intention." />
          <Field
            label="Intentions"
            action={<RegenButton onClick={() => onRegen("intentions")} busy={aiBlock === "intentions"} />}
            hint="Proposées d'après les lectures — modifiables."
          >
            <IntentionsEditor value={doc.prierePuIntentions} onChange={(v) => onPatch({ prierePuIntentions: v })} />
          </Field>
        </div>
      </Card>

      {/* IV · Liturgie eucharistique */}
      <Card eyebrow="IV · Liturgie eucharistique" title="Offertoire, communion, envoi" aside={<SectionIcon><Icons.Chalice size={17} /></SectionIcon>}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <SongPicker label="Chant d'offertoire" moment="offertoire" season={l.seasonKey} catalog={catalog} value={doc.offertoire} onChange={(c) => onPatch({ offertoire: c })} onRegen={() => onRegenSong("offertoire")} regenerating={songAiMoment === "offertoire"} />
          <Field label="Prière sur les offrandes (prêtre)">
            <TextArea rows={2} value={doc.oraisonOffertoire} onChange={(v) => onPatch({ oraisonOffertoire: v })} placeholder="À compléter…" />
          </Field>
          <SongPicker label="Chant de communion" moment="communion" season={l.seasonKey} catalog={catalog} value={doc.communion} onChange={(c) => onPatch({ communion: c })} onRegen={() => onRegenSong("communion")} regenerating={songAiMoment === "communion"} />
          <Field label="Prière après la communion (prêtre)">
            <TextArea rows={2} value={doc.oraisonCommunion} onChange={(v) => onPatch({ oraisonCommunion: v })} placeholder="À compléter…" />
          </Field>
          <SongPicker label="Chant d'envoi" moment="envoi" season={l.seasonKey} catalog={catalog} value={doc.envoi} onChange={(c) => onPatch({ envoi: c })} onRegen={() => onRegenSong("envoi")} regenerating={songAiMoment === "envoi"} />
        </div>
      </Card>

      {/* Action bar — fixed on desktop; on phones it sits in-flow at the bottom of
          the page (scrolls with the content) so it never covers the form. */}
      <div
        style={{
          display: "flex", justifyContent: "center",
          ...(isMobile
            ? { borderTop: "1px solid var(--border)", marginTop: 8, paddingTop: 14 }
            : { position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 25, background: "color-mix(in srgb, var(--bg-raised) 94%, transparent)", backdropFilter: "blur(8px)", borderTop: "1px solid var(--border)", padding: "10px 0" }),
        }}
      >
        <div style={{ width: isMobile ? "100%" : "min(760px, 92vw)", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", gap: isMobile ? "6px" : "12px 16px" }}>
          {/* Guidance — desktop only */}
          {!isMobile && (
            <div style={{ flex: "1 1 250px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", color: "var(--text-muted)", lineHeight: 1.35 }}>
                Une fois finalisé, il faut faire valider la feuille animateur par le Père.
              </span>
              <span style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", color: "var(--text-muted)", lineHeight: 1.35 }}>
                Si besoin, transmettre la feuille assemblée au Père pour impression après vérification.
              </span>
              {saveLabel && <span style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", color: "var(--text-faint)", whiteSpace: "nowrap" }}>{saveLabel}</span>}
            </div>
          )}
          {isMobile && saveLabel && <span style={{ marginLeft: 2, fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", color: "var(--text-faint)" }}>{saveLabel}</span>}

          {/* Documents (+ WhatsApp on mobile) — one line */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Button variant="secondary" size="sm" iconLeft={<Icons.Music size={15} />} onClick={onDownloadFeuille} style={{ whiteSpace: "nowrap", flex: isMobile ? "1 1 0" : "none", justifyContent: "center", minWidth: 0 }}>
              Feuille assemblée
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<Icons.Download size={15} />} onClick={onDownload} style={{ whiteSpace: "nowrap", flex: isMobile ? "1 1 0" : "none", justifyContent: "center", minWidth: 0 }}>
              Feuille animateur
            </Button>
            {isMobile && (
              <IconButton variant="secondary" aria-label="Partager la feuille au Père (WhatsApp)" onClick={onShare} style={{ flexShrink: 0, color: "#25D366", borderColor: "color-mix(in srgb, #25D366 35%, var(--border))" }}>
                <WhatsAppGlyph size={18} />
              </IconButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
